// Conferência da rota de cobrança: o que impede uma parada de ir para a rua.
//
// POR QUE ISTO EXISTE
//
// O cobrador sai de carro para bater na porta de quem deve. Se ele chega sem o
// boleto, não tem como receber; se chega sem a fatura da Equatorial, não tem como
// provar a dívida; se chega no endereço errado, perdeu a viagem. Antes disto a
// rota era montada e mandada sem ninguém olhar nada disso.
//
// POR QUE O GATE NÃO EXIGE TUDO
//
// Medido em produção em 14/09/2026, sobre as 800 UCs com fatura vencida:
// só 215 (27%) têm boleto da Vigor + PDF da Equatorial em TODAS as faturas em
// aberto, e só 27 (3,4%) têm isso E termo de adesão. Exigir o pacote completo
// reprovaria 96,6% das paradas — a rota deixaria de existir.
//
// Então a regra é a que o gestor pediu, "colocar ou justificar", com um peso
// diferente por idade da fatura:
//
//   - fatura MAIS RECENTE incompleta  -> pendência FORTE. É a que ele vai cobrar;
//                                        sair sem ela exige justificativa escrita
//                                        de verdade (mínimo de caracteres).
//   - faturas ANTIGAS incompletas     -> pendência fraca. Entram na mesma
//                                        justificativa, sem travar sozinhas.
//   - termo de adesão ausente         -> resposta obrigatória, mas "não existe"
//                                        é resposta válida: em 72% das UCs é a
//                                        verdade, o cliente é antigo e nunca
//                                        assinou. O que não se aceita é sair sem
//                                        ter olhado.

import type { FaturaEmAberto } from './rotaCobranca';
// Import de VALOR: a definição de "avulso" é a mesma do gate de emissão, e ter
// duas cópias da regra é como as telas passam a discordar. `janelaFaturamento`
// só depende de funções de data, então não fecha ciclo nem puxa o supabase.
import { ehBoletoAvulso } from './janelaFaturamento.ts';
import { ucCodigosDaFatura } from './ucCodigo.ts';

/** O que pode faltar numa fatura para o cobrador conseguir trabalhar com ela. */
export type FaltaMaterial = 'boleto_vigor' | 'fatura_equatorial';

export const ROTULO_FALTA: Record<FaltaMaterial, string> = {
    boleto_vigor: 'boleto da Vigor',
    fatura_equatorial: 'fatura da Equatorial',
};

/** Caracteres mínimos da justificativa. Curto o bastante para não irritar, longo
 *  o bastante para "ok" e "." não passarem — que é o que acontece quando o campo
 *  é livre e ninguém lê depois. */
export const MIN_JUSTIFICATIVA = 15;

/** Mapa `fatura_id` -> caminho do PDF anexado na conferência. */
export type AnexosMaterial = Record<string, string>;

/**
 * Tem o BOLETO da Vigor — o documento, não um jeito de pagar.
 *
 * Correção do gestor em 15/09/2026. A primeira versão aceitava linha digitável ou
 * PIX como "tem boleto", com o argumento de que o cliente paga no app do banco com
 * o número. Está errado para quem bate na porta: o cobrador precisa do documento
 * para entregar e para mostrar o que está sendo cobrado. Linha digitável solta é um
 * número de 47 dígitos que ele teria que ditar.
 *
 * É também o que a própria tela já dizia: o botão "Boleto" só aparece com um destes
 * três campos. Fatura com o botão ausente é fatura sem boleto, e era isso que o
 * checklist estava deixando passar.
 *
 * Boleto que falta se EMITE (o link de emissão está na conferência) — não se anexa.
 */
export const temBoletoVigor = (f: FaturaEmAberto): boolean =>
    !!(f.url_boleto || f.url_boleto_combinado || f.pdf_boleto_combinado);

/**
 * Dá para pagar de algum jeito, mesmo sem o documento?
 *
 * Não substitui o boleto no checklist — serve para a tela diferenciar "não tem nada"
 * de "tem como pagar, falta o papel". São conversas diferentes na porta do cliente.
 */
export const temMeioDePagamento = (f: FaturaEmAberto): boolean =>
    !!(f.linha_digitavel_boleto || f.codigo_pix_boleto);

/** Tem como provar a dívida? O anexo da conferência conta igual ao PDF do sistema. */
export const temFaturaEquatorial = (f: FaturaEmAberto, anexos?: AnexosMaterial | null): boolean =>
    !!(f.pdf_fatura || anexos?.[String(f.id)]);

/**
 * Cobrança avulsa: parcela de acordo, acerto, saldo de cancelamento.
 *
 * Ela NÃO nasce de uma fatura da Equatorial — foi a Vigor que lançou o valor. Não
 * existe PDF da distribuidora para ela em lugar nenhum, hoje nem depois, e o
 * checklist pedia um assim mesmo: as três parcelas do acordo do Eurivan
 * (R$ 2.104,20/mês) apareciam em vermelho como "falta fatura da Equatorial" e
 * obrigavam a justificar o que ninguém tem como resolver.
 *
 * O que prova essa dívida é o termo do acordo, e disso cuida a seção 3.
 */
export const ehCobrancaAvulsa = (f: FaturaEmAberto): boolean =>
    ehBoletoAvulso(f.classificacao);

/**
 * A fatura da distribuidora é exigível nesta fatura?
 *
 * Só quando ela veio de uma: cobrança avulsa está dispensada. Quem já tem o PDF
 * (algumas avulsas antigas herdaram um) continua mostrando o documento — o que
 * muda é não cobrar o que não existe.
 */
export const exigeFaturaEquatorial = (f: FaturaEmAberto): boolean => !ehCobrancaAvulsa(f);

export const faltasDaFatura = (
    f: FaturaEmAberto,
    anexos?: AnexosMaterial | null,
): FaltaMaterial[] => {
    const faltas: FaltaMaterial[] = [];
    if (!temBoletoVigor(f)) faltas.push('boleto_vigor');
    if (exigeFaturaEquatorial(f) && !temFaturaEquatorial(f, anexos)) faltas.push('fatura_equatorial');
    return faltas;
};

export interface PendenciaFatura {
    faturaId: string;
    mes: string | null;
    vencimento: string | null;
    /** De qual UC da parada é a fatura — ver `FaturaEmAberto.uc_ref`. */
    ucRef?: string | null;
    faltas: FaltaMaterial[];
    /** A mais recente (da sua UC) é a que o cobrador vai cobrar de verdade. */
    recente: boolean;
}

export interface AvaliacaoMaterial {
    /** Toda fatura em aberto tem boleto e fatura da distribuidora. */
    completo: boolean;
    /** Faltou algo na fatura MAIS RECENTE — o caso grave. */
    faltaNaRecente: boolean;
    pendencias: PendenciaFatura[];
    /** Sem nenhuma fatura em aberto. Não é pendência: é UC que já foi paga entre
     *  a montagem e a conferência, e a tela precisa dizer isso em vez de mostrar
     *  um checklist vazio que parece bug. */
    semFaturas: boolean;
}

/**
 * O que falta para esta parada, olhando todas as faturas em aberto.
 *
 * `faturas` vem de `faturasAtuaisDaUc`, que já ordena por vencimento crescente —
 * a última é a mais recente. Reordenar aqui protege de quem chamar fora de ordem.
 *
 * "Mais recente" é por UC (`uc_ref`): numa parada que junta duas UCs do mesmo
 * cliente, cada uma tem a sua fatura do mês, e as duas vão ser cobradas.
 */
export const avaliarMaterial = (
    faturas: FaturaEmAberto[],
    anexos?: AnexosMaterial | null,
): AvaliacaoMaterial => {
    const lista = [...(faturas || [])].sort(
        (a, b) => String(a.vencimento || '').localeCompare(String(b.vencimento || '')));

    if (lista.length === 0) {
        return { completo: true, faltaNaRecente: false, pendencias: [], semFaturas: true };
    }

    // A lista está em ordem crescente: a última vista de cada UC é a mais recente.
    const recentePorUc = new Map<string, string>();
    lista.forEach(f => recentePorUc.set(String(f.uc_ref ?? ''), String(f.id)));
    const idsRecentes = new Set(recentePorUc.values());
    const pendencias: PendenciaFatura[] = [];

    lista.forEach(f => {
        const faltas = faltasDaFatura(f, anexos);
        if (faltas.length === 0) return;
        pendencias.push({
            faturaId: String(f.id),
            mes: f.mes_referencia,
            vencimento: f.vencimento,
            ucRef: f.uc_ref ?? null,
            faltas,
            recente: idsRecentes.has(String(f.id)),
        });
    });

    return {
        completo: pendencias.length === 0,
        faltaNaRecente: pendencias.some(p => p.recente),
        pendencias,
        semFaturas: false,
    };
};

/** Resumo de uma linha para o ícone/tooltip da lista de faturas em aberto. */
export const resumoPendencia = (a: AvaliacaoMaterial): string => {
    if (a.semFaturas) return 'Nada em aberto hoje';
    if (a.completo) return 'Boleto e fatura da Equatorial em todas as faturas';
    const n = a.pendencias.length;
    const faturas = `${n} ${n === 1 ? 'fatura' : 'faturas'} sem material completo`;
    return a.faltaNaRecente ? `${faturas} — inclusive a mais recente` : faturas;
};

// ---------------------------------------------------------------------------
// O que falta, dito com todas as letras
// ---------------------------------------------------------------------------
//
// Pedido do Vinicius em 16/09/2026: "quando tiver faltando alguma coisa, destaca
// em vermelho especificamente o que está faltando, deixa explícito". A tela
// dizia "3 · Termo de adesão" em âmbar e um texto cinza lá embaixo — quem
// conferia 34 paradas não via, parada a parada, o que era o problema.

export type TipoFalta = 'endereco' | 'boleto_vigor' | 'fatura_equatorial' | 'termo';

/** Carimbo curto, para a coluna de paradas. */
export const ROTULO_CURTO_FALTA: Record<TipoFalta, string> = {
    endereco: 'sem localização',
    boleto_vigor: 'sem boleto',
    fatura_equatorial: 'sem fatura',
    termo: 'sem termo',
};

export interface FaltaDetalhada {
    tipo: TipoFalta;
    /** A frase inteira: o que falta e de qual fatura/UC. */
    detalhe: string;
    /** O carimbo. Diz "em 1 de 3 UCs" quando a falta é só de parte da parada —
     *  "sem termo" seco, com o termo aparecendo ao abrir, parecia erro. */
    curto: string;
}

/** Uma UC da parada, para decidir se algum termo de adesão a cobre. */
export interface UcTermo {
    /** O código como está na parada. */
    codigo: string;
    /** `numero_uc` e `outro_numero_uc` do cadastro (podem vir compostos, "A / B"). */
    numerosCadastro?: Array<string | null | undefined>;
    temTermo: boolean;
}

/**
 * As UCs da parada sem termo de adesão.
 *
 * Uma UC também conta como coberta quando outra UC da mesma parada, COM termo,
 * divide um número com ela: é a mesma unidade cadastrada duas vezes. Caso da
 * Super Gelo (23/09/2026): a parada juntava a UC 453 (355307501202 / 10025066305,
 * termo CS-A098125E) e o cadastro 1761 — duplicado cancelado, mesmos números,
 * sem termo. O card dizia "sem termo" de um cliente com termo assinado.
 */
export const ucsSemTermo = (ucs: UcTermo[]): string[] => {
    const numeros = (u: UcTermo) => [u.codigo, ...(u.numerosCadastro ?? [])].flatMap(n => ucCodigosDaFatura(n));
    const cobertos = new Set(ucs.filter(u => u.temTermo).flatMap(numeros));
    return ucs
        .filter(u => !u.temTermo && !numeros(u).some(n => cobertos.has(n)))
        .map(u => u.codigo);
};

export interface EntradaFaltas {
    temEndereco: boolean;
    temCoordenada: boolean;
    avaliacao: AvaliacaoMaterial;
    /** Todas as UCs cobradas na parada, a principal primeiro. */
    ucs: string[];
    /** As que não têm termo achado pelo sistema. */
    ucsSemTermo: string[];
}

const listaMeses = (ps: PendenciaFatura[], comUc: boolean): string =>
    ps.map(p => {
        const mes = p.mes || (p.vencimento ? `venc. ${p.vencimento.split('-').reverse().join('/')}` : 'sem mês');
        return comUc && p.ucRef ? `${mes} (UC ${p.ucRef})` : mes;
    }).join(', ');

/**
 * Tudo o que falta na parada, na ordem em que o cobrador tropeçaria nisso: chegar,
 * cobrar, provar a dívida, provar o contrato.
 *
 * Olha o DADO, não a resposta da conferência: "sem termo" continua sendo falta
 * mesmo depois de alguém responder "não existe" — a resposta libera a parada,
 * mas o cobrador precisa saber que vai sem contrato.
 */
export const faltasDetalhadas = (e: EntradaFaltas): FaltaDetalhada[] => {
    const faltas: FaltaDetalhada[] = [];
    const variasUcs = e.ucs.length > 1;
    const curto = ROTULO_CURTO_FALTA;

    if (!e.temEndereco) {
        faltas.push({ tipo: 'endereco', detalhe: 'Sem endereço no cadastro', curto: 'sem endereço' });
    } else if (!e.temCoordenada) {
        faltas.push({ tipo: 'endereco', detalhe: 'Sem localização no mapa — o cobrador não consegue navegar até lá', curto: curto.endereco });
    }

    const semBoleto = e.avaliacao.pendencias.filter(p => p.faltas.includes('boleto_vigor'));
    if (semBoleto.length > 0) {
        faltas.push({ tipo: 'boleto_vigor', detalhe: `Boleto da Vigor: ${listaMeses(semBoleto, variasUcs)}`, curto: curto.boleto_vigor });
    }
    const semFatura = e.avaliacao.pendencias.filter(p => p.faltas.includes('fatura_equatorial'));
    if (semFatura.length > 0) {
        faltas.push({ tipo: 'fatura_equatorial', detalhe: `Fatura da Equatorial: ${listaMeses(semFatura, variasUcs)}`, curto: curto.fatura_equatorial });
    }

    if (e.ucsSemTermo.length > 0) {
        const parcial = variasUcs && e.ucsSemTermo.length < e.ucs.length;
        faltas.push({
            tipo: 'termo',
            detalhe: !variasUcs
                ? 'Termo de adesão não encontrado'
                : !parcial
                    ? 'Termo de adesão não encontrado em nenhuma das UCs'
                    : `Termo de adesão da UC ${e.ucsSemTermo.join(', UC ')}`,
            curto: parcial ? `${curto.termo} em ${e.ucsSemTermo.length} de ${e.ucs.length} UCs` : curto.termo,
        });
    }
    return faltas;
};

// ---------------------------------------------------------------------------
// Estado da conferência de uma parada
// ---------------------------------------------------------------------------

export type EnderecoSituacao = 'confirmado' | 'corrigido';
export type MaterialSituacao = 'completo' | 'justificado';
export type TermoSituacao = 'tem' | 'sem_termo' | 'anexado';

/** O subconjunto de `rota_cobranca_paradas` que a conferência lê e escreve. */
export interface ConferenciaParada {
    endereco_situacao?: EnderecoSituacao | null;
    material_situacao?: MaterialSituacao | null;
    material_justificativa?: string | null;
    termo_situacao?: TermoSituacao | null;
    conferida_em?: string | null;
}

export type MotivoPendente =
    | 'endereco'
    | 'material'
    | 'justificativa_curta'
    | 'termo';

export const ROTULO_PENDENCIA: Record<MotivoPendente, string> = {
    endereco: 'confirmar o endereço no mapa',
    material: 'resolver o material de cobrança',
    justificativa_curta: `justificativa com pelo menos ${MIN_JUSTIFICATIVA} caracteres`,
    termo: 'responder sobre o termo de adesão',
};

/**
 * O que ainda falta nesta parada. Lista vazia = pronta para ir para a rua.
 *
 * A justificativa curta é motivo à parte de propósito: "material não resolvido" e
 * "você escreveu 'ok'" são erros diferentes e a tela precisa dizer qual é.
 */
export const pendenciasDaParada = (p: ConferenciaParada): MotivoPendente[] => {
    const faltando: MotivoPendente[] = [];
    if (!p.endereco_situacao) faltando.push('endereco');

    if (!p.material_situacao) {
        faltando.push('material');
    } else if (p.material_situacao === 'justificado'
        && String(p.material_justificativa || '').trim().length < MIN_JUSTIFICATIVA) {
        faltando.push('justificativa_curta');
    }

    if (!p.termo_situacao) faltando.push('termo');
    return faltando;
};

export const paradaConferida = (p: ConferenciaParada): boolean =>
    pendenciasDaParada(p).length === 0;

export interface SituacaoRota {
    /** Dá para liberar a rota para o cobrador. */
    liberavel: boolean;
    conferidas: number;
    total: number;
    /** Posições das paradas que ainda travam, para a tela apontar onde ir. */
    posicoesPendentes: number[];
}

export const situacaoDaRota = (
    paradas: Array<ConferenciaParada & { posicao: number }>,
): SituacaoRota => {
    const lista = paradas || [];
    const pendentes = lista.filter(p => !paradaConferida(p));
    return {
        // Rota sem parada nenhuma não é liberável: liberar o nada confundiria
        // mais do que ajudaria, e só acontece se todas as paradas forem apagadas.
        liberavel: lista.length > 0 && pendentes.length === 0,
        conferidas: lista.length - pendentes.length,
        total: lista.length,
        posicoesPendentes: pendentes.map(p => p.posicao).sort((a, b) => a - b),
    };
};

// ---------------------------------------------------------------------------
// Coordenada colada à mão
// ---------------------------------------------------------------------------

/**
 * Lê uma coordenada como o usuário cola do Google Maps.
 *
 * O que chega na prática: "-16.6799, -49.2550", "-16.6799,-49.2550", o texto
 * copiado do balão do Maps, ou a URL inteira (`.../@-16.6799,-49.2550,17z` e
 * `?q=-16.6799,-49.2550`). Os quatro precisam funcionar, senão a pessoa desiste
 * e a parada vai errada do mesmo jeito.
 *
 * Devolve null quando não há par plausível — inclusive quando os números estão
 * fora do Brasil, que é o sintoma de ter colado latitude e longitude trocadas.
 */
export const lerCoordenadaColada = (texto: string): { lat: number; lng: number } | null => {
    const bruto = String(texto || '').trim();
    if (!bruto) return null;

    // Pega o primeiro par "número, número" que aparecer — funciona igual no texto
    // solto e na URL, porque em ambas o par vem junto.
    const m = /(-?\d{1,3}(?:[.,]\d+)?)\s*[,;]\s*(-?\d{1,3}(?:[.,]\d+)?)/.exec(bruto);
    if (!m) return null;

    // Vírgula decimal vira ponto. Só quando o número tem UMA vírgula e nenhum
    // ponto — "-16,6799" é decimal, mas "-16.6799" já está certo.
    const numero = (v: string): number => {
        const limpo = v.includes('.') ? v : v.replace(',', '.');
        return Number(limpo);
    };

    const lat = numero(m[1]);
    const lng = numero(m[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

    // Goiás fica em lat -19..-12 e lng -53..-45. Fora do Brasil inteiro é erro
    // de digitação ou par invertido, e mandar o cobrador para o oceano Atlântico
    // é pior do que recusar.
    if (lat < -34 || lat > 6 || lng < -74 || lng > -34) return null;

    return { lat, lng };
};
